import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ChevronDown, Star } from "lucide-react";
import { HERO_SMILE } from "../data/content";
import { scrollToId } from "../lib/scroll";
import { CountUp, Magnetic } from "./fx";

const EASE = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, delay }) => (
  <span className="hero-mask-line">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "15+", label: "Years of Excellence" },
  { value: "11", label: "Specialities" },
  { value: "5.0", label: "Google Rated", star: true },
  { value: "100%", label: "Pain-free Care" },
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="grain relative flex min-h-[100svh] items-center overflow-hidden bg-[#070B12]"
    >
      <video
        data-testid="hero-background-video"
        className="absolute inset-0 h-full w-full object-cover"
        poster={HERO_SMILE}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        aria-hidden
      >
        <source src="/assets/hero.webm" type="video/webm" />
        <source src="/assets/hero.mp4" type="video/mp4" />
      </video>
      <div
        aria-hidden
        className="absolute inset-0 bg-[#070B12]/45"
      />
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-r from-[#070B12]/95 via-[#070B12]/75 to-[#070B12]/45"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#070B12] to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[#070B12]/80 to-transparent"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-40 top-1/4 h-[520px] w-[520px] animate-glow-drift rounded-full bg-toothcyan/[0.1] blur-3xl"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-36 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 backdrop-blur-md"
          data-testid="hero-credential-badge"
        >
          <Sparkles className="h-3.5 w-3.5 text-ember" />
          <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-100 sm:text-[11px]">
            Prof. Dr. Swagat Panda · MDS · Endodontist
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-white sm:text-6xl lg:text-7xl"
        >
          <MaskLine delay={0.15}>Sculpting healthier</MaskLine>
          <MaskLine delay={0.27}>
            teeth &amp;{" "}
            <em className="bg-gradient-to-r from-toothcyan via-sky-200 to-amber-200 bg-clip-text font-medium italic text-transparent">
              everlasting
            </em>
          </MaskLine>
          <MaskLine delay={0.39}>smiles.</MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
          className="mt-7 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg"
          data-testid="hero-subheadline"
        >
          Painless multi-speciality dentistry in the heart of Saheed Nagar,
          Bhubaneswar.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.68, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Magnetic strength={0.3}>
            <button
              data-testid="hero-book-appointment-button"
              onClick={() => scrollToId("#book")}
              className="group flex items-center gap-2.5 rounded-full bg-gradient-to-r from-ember to-amber-500 px-7 py-3.5 text-sm font-bold text-[#070B12] shadow-[0_0_28px_rgba(232,178,92,0.3)] transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(232,178,92,0.5)] active:scale-[0.97]"
            >
              Book an Appointment
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </Magnetic>
          <button
            data-testid="hero-smile-gallery-button"
            onClick={() => scrollToId("#gallery")}
            className="rounded-full border border-white/25 bg-white/[0.06] px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-md transition-all duration-200 hover:border-toothcyan/60 hover:bg-white/[0.1] active:scale-[0.97]"
          >
            View Smile Transformations
          </button>
        </motion.div>

        <div
          className="mt-14 flex max-w-2xl flex-wrap gap-3"
          data-testid="hero-stats-row"
        >
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85 + i * 0.1, ease: EASE }}
              className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.05] px-4 py-3 backdrop-blur-md transition-colors duration-300 hover:border-ember/40"
            >
              <div className="flex items-center gap-1 font-serif text-xl font-semibold text-white sm:text-2xl">
                <CountUp value={s.value} />
                {s.star && <Star className="h-4 w-4 fill-ember text-ember" />}
              </div>
              <div className="font-mono text-[8px] uppercase leading-snug tracking-[0.14em] text-mist sm:text-[9px]">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-1.5 text-mist transition-colors hover:text-white"
        aria-label="Scroll to explore"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.3em]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 7, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
}
